/**
 * DetailerQualificationForm — create + edit form for AISC §1.7.0 §3.1
 * detailer qualification records.
 *
 * Redesigned 2026-05-05 (Phase 2.4, Class P) onto the PaperForm helper
 * library. AISC 207-25 §1.7.0 §3.1 + prereq.
 *
 * URLs: /detailer-qualifications/new | /detailer-qualifications/:id
 *
 * Per the model invariant, EITHER user_id (internal) OR
 * subcontractor_name + subcontractor_contact_email (external) — never both.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import {
  Stack, Typography, Button, Alert, Box, Grid,
  TextField, MenuItem, RadioGroup, Radio, FormControlLabel,
  Breadcrumbs, Link as MuiLink, IconButton, Divider, Paper,
} from "@mui/material";
import { HomeRounded, ArrowBackRounded, EngineeringOutlined, BlockRounded } from "@mui/icons-material";

import {
  getDetailerQualification,
  createDetailerQualification,
  updateDetailerQualification,
  revokeDetailerQualification,
} from "../../utils/detailerQualificationsApi";
import {
  PaperFormCard, PaperFormHeader, PaperFormSection, PaperFormFooter,
  FieldLabel,
} from "../../components/PaperForm";

const QUALIFICATION_TYPE_PRESETS = [
  "AISC Certified Detailer",
  "P.E. Structural",
  "P.E. Mechanical",
  "Trade School Detailing Certificate",
  "Other (free text)",
];

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "EXPIRED", label: "Expired" },
  { value: "REVOKED", label: "Revoked" },
  { value: "PENDING_RENEWAL", label: "Pending renewal" },
];

export default function DetailerQualificationForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isCreate = !id || id === "new";

  // Attribution mode: 'internal' (user_id) or 'external' (subcontractor)
  const [attributionMode, setAttributionMode] = useState("external");
  const [userId, setUserId] = useState("");
  const [subcontractorName, setSubcontractorName] = useState("");
  const [subcontractorEmail, setSubcontractorEmail] = useState("");

  // Qualification details
  const [qualificationType, setQualificationType] = useState("");
  const [qualificationTypeOther, setQualificationTypeOther] = useState("");
  const [issuingBody, setIssuingBody] = useState("");
  const [qualificationNumber, setQualificationNumber] = useState("");
  const [qualificationDate, setQualificationDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [certificateFilename, setCertificateFilename] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [notes, setNotes] = useState("");

  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");

  useEffect(() => {
    if (isCreate) return;
    let cancelled = false;
    setLoading(true);
    getDetailerQualification(id)
      .then((q) => {
        if (cancelled) return;
        if (q.user_id) {
          setAttributionMode("internal");
          setUserId(String(q.user_id));
        } else {
          setAttributionMode("external");
          setSubcontractorName(q.subcontractor_name || "");
          setSubcontractorEmail(q.subcontractor_contact_email || "");
        }
        const isPreset = QUALIFICATION_TYPE_PRESETS.includes(q.qualification_type);
        setQualificationType(isPreset ? q.qualification_type : "Other (free text)");
        setQualificationTypeOther(isPreset ? "" : q.qualification_type);
        setIssuingBody(q.issuing_body || "");
        setQualificationNumber(q.qualification_number || "");
        setQualificationDate(q.qualification_date || "");
        setExpirationDate(q.expiration_date || "");
        setCertificateFilename(q.certificate_filename || "");
        setStatus(q.status || "ACTIVE");
        setNotes(q.notes || "");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || err?.message || "Failed to load qualification.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, isCreate]);

  const validate = () => {
    if (attributionMode === "internal" && !userId) return "Internal user_id is required.";
    if (attributionMode === "external" && (!subcontractorName.trim() || !subcontractorEmail.trim()))
      return "Subcontractor name and contact email are both required.";
    const finalType = qualificationType === "Other (free text)" ? qualificationTypeOther : qualificationType;
    if (!finalType) return "Qualification type is required.";
    if (!qualificationDate) return "Qualification date is required.";
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setSaving(true);
    setError(null);
    const finalType = qualificationType === "Other (free text)" ? qualificationTypeOther : qualificationType;
    const payload = {
      user_id: attributionMode === "internal" ? Number(userId) : null,
      subcontractor_name: attributionMode === "external" ? subcontractorName : null,
      subcontractor_contact_email: attributionMode === "external" ? subcontractorEmail : null,
      qualification_type: finalType,
      issuing_body: issuingBody || null,
      qualification_number: qualificationNumber || null,
      qualification_date: qualificationDate,
      expiration_date: expirationDate || null,
      certificate_filename: certificateFilename || null,
      notes: notes || null,
    };
    try {
      if (isCreate) {
        await createDetailerQualification(payload);
      } else {
        // Edit mode — exclude fields that would change attribution
        const { user_id, subcontractor_name, subcontractor_contact_email, ...editable } = payload;
        await updateDetailerQualification(id, { ...editable, status });
      }
      navigate("/detailer-qualifications");
    } catch (err) {
      setSaving(false);
      setError(err?.response?.data?.error || err?.message || "Save failed.");
    }
  };

  const handleRevoke = async () => {
    if (!revokeReason || revokeReason.trim().length < 4) {
      setError("Revoke reason required (min 4 characters).");
      return;
    }
    setSaving(true);
    try {
      await revokeDetailerQualification(id, revokeReason);
      navigate("/detailer-qualifications");
    } catch (err) {
      setSaving(false);
      setError(err?.response?.data?.error || err?.message || "Revoke failed.");
    }
  };

  const headerName = useMemo(() => {
    if (attributionMode === "internal") return userId ? `User #${userId}` : "Internal";
    return subcontractorName || "External";
  }, [attributionMode, userId, subcontractorName]);

  const qualDate = useMemo(() => {
    if (qualificationDate) return new Date(qualificationDate).toLocaleDateString();
    return new Date().toLocaleDateString();
  }, [qualificationDate]);

  if (loading) {
    return (
      <Box sx={{ pt: 6, textAlign: "center" }}>
        <Typography color="text.secondary">Loading detailer qualification…</Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isCreate ? "New Detailer Qualification" : `Detailer Qualification #${id}`} | SFM</title>
      </Helmet>

      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: "md", mx: "auto", pt: 3, px: { xs: 2, md: 3 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Breadcrumbs>
              <MuiLink href="/" color="inherit" underline="hover">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <HomeRounded fontSize="small" /><span>Home</span>
                </Stack>
              </MuiLink>
              <MuiLink href="/detailer-qualifications" color="inherit" underline="hover">
                Detailer Qualifications
              </MuiLink>
              <Typography color="text.primary">{isCreate ? "New" : `#${id}`}</Typography>
            </Breadcrumbs>
            <IconButton onClick={() => navigate("/detailer-qualifications")} aria-label="Back">
              <ArrowBackRounded />
            </IconButton>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        </Box>

        <PaperFormCard>
          <PaperFormHeader
            formId="SFM · Detailer Qualification"
            title={isCreate ? "Add Detailer Qualification" : "Edit Detailer Qualification"}
            subtitle="AISC §1.7.0 §3.1 — detailer competence record"
            status={status}
            number={headerName}
            numberLabel="Detailer"
            date={qualDate}
            icon="engineering"
          />

          {/* Attribution toggle */}
          <PaperFormSection
            label="Attribution"
            hint="internal = A3 employee · external = subcontractor (mutually exclusive)"
          >
            <Box>
              <RadioGroup
                row
                value={attributionMode}
                onChange={(e) => setAttributionMode(e.target.value)}
              >
                <FormControlLabel
                  value="internal"
                  control={<Radio size="small" disabled={!isCreate} />}
                  label="Internal employee"
                />
                <FormControlLabel
                  value="external"
                  control={<Radio size="small" disabled={!isCreate} />}
                  label="External subcontractor"
                />
              </RadioGroup>
            </Box>

            {attributionMode === "internal" ? (
              <Box>
                <FieldLabel hint="users.id from the users table">User ID</FieldLabel>
                <TextField
                  fullWidth size="small"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  disabled={!isCreate}
                  placeholder="e.g., 142"
                />
              </Box>
            ) : (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <FieldLabel required>Subcontractor / Firm Name</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    value={subcontractorName}
                    onChange={(e) => setSubcontractorName(e.target.value)}
                    disabled={!isCreate}
                    placeholder="e.g., Acme Detailing LLC"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FieldLabel required>Contact Email</FieldLabel>
                  <TextField
                    fullWidth size="small" type="email"
                    value={subcontractorEmail}
                    onChange={(e) => setSubcontractorEmail(e.target.value)}
                    disabled={!isCreate}
                    placeholder="contact@example.com"
                  />
                </Box>
              </Stack>
            )}
          </PaperFormSection>

          <Divider sx={{ mb: 4 }} />

          {/* QUALIFICATION DETAILS */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <PaperFormSection label="Qualification" bottomPadding={0}>
                <Box>
                  <FieldLabel required>Type</FieldLabel>
                  <TextField
                    select fullWidth size="small"
                    value={qualificationType}
                    onChange={(e) => setQualificationType(e.target.value)}
                  >
                    <MenuItem value=""><em>Select type…</em></MenuItem>
                    {QUALIFICATION_TYPE_PRESETS.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                {qualificationType === "Other (free text)" && (
                  <Box>
                    <FieldLabel>Specify Other Type</FieldLabel>
                    <TextField
                      fullWidth size="small"
                      value={qualificationTypeOther}
                      onChange={(e) => setQualificationTypeOther(e.target.value)}
                      placeholder="Describe the qualification"
                    />
                  </Box>
                )}
                <Box>
                  <FieldLabel hint="organization that issued the cert">
                    Issuing Body
                  </FieldLabel>
                  <TextField
                    fullWidth size="small"
                    value={issuingBody}
                    onChange={(e) => setIssuingBody(e.target.value)}
                    placeholder="e.g., American Institute of Steel Construction"
                  />
                </Box>
                <Box>
                  <FieldLabel>Cert / License #</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    value={qualificationNumber}
                    onChange={(e) => setQualificationNumber(e.target.value)}
                    placeholder="e.g., AISC-CD-12345"
                  />
                </Box>
              </PaperFormSection>
            </Grid>

            <Grid item xs={12} md={6}>
              <PaperFormSection label="Validity & Evidence" bottomPadding={0}>
                <Box>
                  <FieldLabel required>Qualification Date</FieldLabel>
                  <TextField
                    fullWidth size="small" type="date"
                    InputLabelProps={{ shrink: true }}
                    value={qualificationDate}
                    onChange={(e) => setQualificationDate(e.target.value)}
                  />
                </Box>
                <Box>
                  <FieldLabel hint="leave blank if non-expiring">
                    Expiration Date
                  </FieldLabel>
                  <TextField
                    fullWidth size="small" type="date"
                    InputLabelProps={{ shrink: true }}
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                  />
                </Box>
                <Box>
                  <FieldLabel hint="under /uploads/detailer_certs/">
                    Certificate Filename
                  </FieldLabel>
                  <TextField
                    fullWidth size="small"
                    value={certificateFilename}
                    onChange={(e) => setCertificateFilename(e.target.value)}
                    placeholder="e.g., aisc_cd_smith_2024.pdf"
                  />
                </Box>
                {!isCreate && (
                  <Box>
                    <FieldLabel>Status</FieldLabel>
                    <TextField
                      select fullWidth size="small"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                )}
              </PaperFormSection>
            </Grid>
          </Grid>

          <PaperFormSection label="Notes" hint="optional · context, scope of work, project assignments">
            <TextField
              fullWidth multiline rows={3} size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Certified for connection design only; supervises 2 junior detailers on ABC project"
            />
          </PaperFormSection>

          {/* REVOKE — edit mode only, when not already revoked */}
          {!isCreate && status !== "REVOKED" && (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5, mb: 4,
                bgcolor: "#fff5f5",
                borderColor: "error.light",
                borderRadius: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <BlockRounded fontSize="small" sx={{ color: "error.main" }} />
                <Typography
                  variant="overline"
                  sx={{ color: "error.main", letterSpacing: "0.08em", fontWeight: 700 }}
                >
                  Revoke Qualification
                </Typography>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }}>
                <Box sx={{ flex: 1 }}>
                  <FieldLabel hint="captured in audit_log">
                    Revoke Reason
                  </FieldLabel>
                  <TextField
                    fullWidth size="small"
                    value={revokeReason}
                    onChange={(e) => setRevokeReason(e.target.value)}
                    placeholder="e.g., expired and not renewed; conduct issue; subcontractor terminated"
                  />
                </Box>
                <Button
                  color="error" variant="outlined"
                  onClick={handleRevoke}
                  disabled={saving}
                  sx={{ textTransform: "none", borderRadius: 2, height: 40 }}
                >
                  Revoke
                </Button>
              </Stack>
            </Paper>
          )}

          <PaperFormFooter
            citation="AISC 207-25 §1.7.0 §3.1 — detailer / checker qualification record"
          />

          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 4 }}>
            <Button
              variant="text"
              onClick={() => navigate("/detailer-qualifications")}
              disabled={saving}
              sx={{ color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<EngineeringOutlined />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {saving ? "Saving…" : (isCreate ? "Create Qualification" : "Save changes")}
            </Button>
          </Stack>
        </PaperFormCard>
      </Box>
    </>
  );
}
